import React from "react";
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from "@mui/material";
import { Clear, Close, Download, FilterAlt, Info, Save, Check } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
    canViewStoreOwner,
    checkUserDetailsFromCookie,
} from "../../../api_calls/userManagement";
import ListingDataTableForModal from "../TabularLists/ListingDataTableForModal";
import Pagination from "../LoadMore/Pagination";
import { updateStateData } from "../../../Store/actions";
import {
    ListingCellCenter,
    ListingCellLeft,
    ListingHeaderContent,
    LISTING_ACTION_TO_TABLE_GAP,
} from "../TabularLists/styles";
import {
    HtmlTooltip,
    IconContainer,
    SpaceBetweenStack,
    StyledCheckbox,
    StyledDarkRedInformationTypography,
    StyledWhiteCheckbox,
} from "../StyledComponents/styled";
import {
    LocationFilterHelpIconWithTooltip,
    SortIcon,
    checkTodayDateWithFormat,
} from "../../../api_calls/utils";
import CloseButton from "../Buttons/CloseButton";
import ActionButton, { LightGreyButton, TextButton } from "../Buttons/ActionButton";
import DisabledButton from "../Buttons/DisabledButton";
import MultiSelect from "../Selectors/MultiSelect";
import SingleSelect from "../Selectors/SingleSelect";
import { loadRetailerAccessAvailability } from "../../../api_calls/retailerSelection";
import * as xlsx from "xlsx";

const StoreDetailTable = ({
    open,
    onClose,
    storeSelectionReduxKey,
    storeOptionsList,
    applyChangesOnSelect = true,
    toHandleApplyChanges,
    selectedValues = [],
    title = "Detailed Store Selection",
    disableRestoreFocus = false,
    readOnly = false,
    enableExcelDownload = false,
    excelExportScope = "filtered",
    excelFileName,
    disableApplyWhenEmpty = false,
}) => {
    const storeDetails = useSelector((state) => state.userSelections);
    const dispatchAction = useDispatch();

    const isReduxManaged = applyChangesOnSelect !== false;

    const [storeNameFilter, setStoreNameFilter] = React.useState([]);
    const [locationFilter, setLocationFilter] = React.useState([]);
    const [ownerFilter, setOwnerFilter] = React.useState([]);
    const [listedFilter, setListedFilter] = React.useState(null);
    // MultiSelect like Owner/Store Name — empty selection means "All Results"
    const [retailerFilter, setRetailerFilter] = React.useState([]);
    // Same visibility rule as bottom-nav retailer dropdown (tenant/cyreen/agency + 2+ roots)
    const [showRetailerColumn, setShowRetailerColumn] = React.useState(false);

    const storesList =
        typeof storeOptionsList !== "undefined"
            ? storeOptionsList
            : typeof storeDetails.stores_list !== "undefined"
                ? storeDetails.stores_list
                : [];

    const [tempSelectedStoreIds, setTempSelectedStoreIds] = React.useState([]);

    const showListedFilter =
        typeof storeDetails.listing_check_saved_for_campaign !== "undefined"
            ? storeDetails.listing_check_saved_for_campaign
            : false;

    const uniqueOptions = (options) => {
        const seen = new Set();

        return options.filter((option) => {
            if (seen.has(option.label)) return false;

            seen.add(option.label);
            return true;
        });
    };

    const storeOptions = storesList.map((s) => ({ id: s.id, label: s.name }));
    // Same rule as the Location filter in StoreDetailFilter: Street & No., ZIP Code, City or Region
    const locationValuesOf = (store) =>
        [store.address, store.zipCode, store.city, store.region?.name].filter(Boolean);

    const locationOptions = (() => {
        const seen = new Set();
        const options = [];

        storesList.forEach((store) => {
            locationValuesOf(store).forEach((val) => {
                if (!seen.has(val)) {
                    seen.add(val);
                    options.push({ id: val, label: val });
                }
            });
        });

        return options;
    })();
    const ownerOptions = uniqueOptions(
        storesList.map((s) => ({
            id: s.company?.id,
            label: s.company?.name,
        }))
    );

    // Filter choices come from stores currently in the modal (not from retailer-access API options)
    const retailerFilterOptions = uniqueOptions(
        storesList
            .filter((store) => store.root_retailer_company?.id != null)
            .map((store) => ({
                id: store.root_retailer_company.id,
                label: store.root_retailer_company.name || "",
                value: store.root_retailer_company.id,
            }))
    );

    const hasFilters =
        storeNameFilter.length ||
        locationFilter.length ||
        ownerFilter.length ||
        listedFilter ||
        retailerFilter.length;

    const checkUserDetails = checkUserDetailsFromCookie();
    let userDetailObj = {};

    if (checkUserDetails.success === true) {
        userDetailObj = checkUserDetails.user_detail;
    }

    const loggedInUserType =
        typeof userDetailObj.user_type !== "undefined"
            ? userDetailObj.user_type.toLowerCase()
            : "";

    // Decide whether Retailer column/filter should show (same rule as bottom nav)
    React.useEffect(() => {
        if (!open) {
            return;
        }

        if (loggedInUserType === "") {
            setShowRetailerColumn(false);
            return;
        }

        let shouldApplyResult = true;

        loadRetailerAccessAvailability(loggedInUserType)
            .then((retailerAccessInfo) => {
                if (!shouldApplyResult) {
                    return;
                }
                setShowRetailerColumn(retailerAccessInfo.available === true);
            })
            .catch(() => {
                if (!shouldApplyResult) {
                    return;
                }
                setShowRetailerColumn(false);
            });

        return () => {
            shouldApplyResult = false;
        };
    }, [open, loggedInUserType]);
    const paginationStartValue =
        typeof storeDetails.store_selection_pagination !== "undefined"
            ? storeDetails.store_selection_pagination.store_list_start
            : 0;

    const paginationEndValue =
        typeof storeDetails.store_selection_pagination !== "undefined"
            ? storeDetails.store_selection_pagination.store_list_end
            : 10;

    const [sortConfig, setSortConfig] = React.useState({
        name: { dir: "asc", active: true },
        address: { dir: "asc", active: false },
        zipcode: { dir: "asc", active: false },
        city: { dir: "asc", active: false },
        region: { dir: "asc", active: false },
        owner: { dir: "asc", active: false },
        retailer: { dir: "asc", active: false },
        listed: { dir: "asc", active: false },
        available: { dir: "asc", active: false },
    });

    const handleStoreCheckboxChange = React.useCallback((store, checked) => {
        if (readOnly || store.available === false) return;

        setTempSelectedStoreIds((prev) => {
            if (checked) {
                return prev.includes(store.id) ? prev : [...prev, store.id];
            }

            return prev.filter((id) => id !== store.id);
        });
    }, [readOnly]);

    const clearFilters = () => {
        setStoreNameFilter([]);
        setLocationFilter([]);
        setOwnerFilter([]);
        setListedFilter(null);
        setRetailerFilter([]);

        dispatchAction(
            updateStateData({
                detailed_store_list_filter_applied: false,
                detailed_stores_filtered: [],
            })
        );
    };

    const handleApply = () => {
        if (readOnly) return;

        if (!isReduxManaged) {
            if (typeof toHandleApplyChanges === "function") {
                toHandleApplyChanges(tempSelectedStoreIds, tempSelectedStoreNames, {
                    allSelected: tempAllSelected,
                });
            }
            clearFilters();
            onClose();
            return;
        }

        dispatchAction(
            updateStateData({
                [storeSelectionReduxKey]: {
                    ...storeDetails[storeSelectionReduxKey],
                    store_ids: tempSelectedStoreIds,
                    selected_store_names: tempSelectedStoreNames,
                    all_selected: tempAllSelected,
                },
            })
        );
        clearFilters();
        onClose();
    };

    const handleCancel = () => {
        if (!isReduxManaged) {
            setTempSelectedStoreIds(Array.isArray(selectedValues) ? selectedValues : []);
            clearFilters();
            onClose();
            return;
        }

        const reduxSelection = storeDetails[storeSelectionReduxKey] || {};
        setTempSelectedStoreIds(reduxSelection.store_ids || []);
        clearFilters();
        onClose();
    };

    const applyFilters = () => {
        const filteredStores = storesList.filter((store) => {
            const matchesStoreName =
                storeNameFilter.length === 0 ||
                storeNameFilter.some((filter) => filter.id === store.id);

            const matchesLocation =
                locationFilter.length === 0 ||
                locationValuesOf(store).some((val) =>
                    locationFilter.some((filter) => filter.id === val)
                );

            const matchesOwner =
                ownerFilter.length === 0 ||
                ownerFilter.some((filter) => filter.id === store.company?.id);

            let matchesListed = true;
            if (listedFilter) {
                const selectedListed = listedFilter.value === "yes";
                matchesListed = store.listing_check_saved === selectedListed;
            }

            // Root retailer company — empty Multiselect = all retailers (same as Owner)
            const matchesRetailer =
                retailerFilter.length === 0 ||
                retailerFilter.some(
                    (filter) => filter.id === store.root_retailer_company?.id
                );

            return (
                matchesStoreName &&
                matchesLocation &&
                matchesOwner &&
                matchesListed &&
                matchesRetailer
            );
        });

        dispatchAction(
            updateStateData({
                detailed_store_list_filter_applied: true,
                detailed_stores_filtered: filteredStores,
            })
        );

        return filteredStores;
    };

    const sortedResults = (toBeSortedList) => {
        return toBeSortedList.slice().sort((a, b) => {
            const field = Object.keys(sortConfig).find(
                (key) => sortConfig[key].active === true
            );
            const sortDir = sortConfig[field]?.dir;

            if (field === "name") {
                return sortDir === "asc"
                    ? a.name.localeCompare(b.name)
                    : b.name.localeCompare(a.name);
            }

            if (field === "address") {
                return sortDir === "asc"
                    ? a.address.localeCompare(b.address)
                    : b.address.localeCompare(a.address);
            }

            if (field === "zipcode") {
                return sortDir === "asc"
                    ? String(a.zipCode).localeCompare(String(b.zipCode))
                    : String(b.zipCode).localeCompare(String(a.zipCode));
            }

            if (field === "city") {
                return sortDir === "asc"
                    ? a.city.localeCompare(b.city)
                    : b.city.localeCompare(a.city);
            }

            if (field === "region") {
                return sortDir === "asc"
                    ? (a.region?.name || "").localeCompare(b.region?.name || "")
                    : (b.region?.name || "").localeCompare(a.region?.name || "");
            }

            if (field === "owner") {
                return sortDir === "asc"
                    ? (a.company?.name || "").localeCompare(b.company?.name || "")
                    : (b.company?.name || "").localeCompare(a.company?.name || "");
            }

            if (field === "retailer") {
                const aName = a.root_retailer_company?.name || "";
                const bName = b.root_retailer_company?.name || "";
                return sortDir === "asc"
                    ? aName.localeCompare(bName)
                    : bName.localeCompare(aName);
            }

            if (field === "listed") {
                const aVal = a.listing_check_saved === true ? 1 : 0;
                const bVal = b.listing_check_saved === true ? 1 : 0;
                return sortDir === "asc" ? aVal - bVal : bVal - aVal;
            }

            return 0;
        });
    };

    const listItems =
        typeof storeDetails.detailed_store_list_filter_applied !== "undefined" &&
            storeDetails.detailed_store_list_filter_applied === true
            ? storeDetails.detailed_stores_filtered
            : storesList;

    const sortedListItems = listItems.length !== 0 ? sortedResults(listItems) : [];

    const storeListitems =
        sortedListItems.length !== 0
            ? sortedListItems.slice(paginationStartValue, paginationEndValue)
            : [];

    const selectableStores = React.useMemo(
        () => listItems.filter((store) => store.available !== false),
        [listItems]
    );

    const selectableStoreIds = React.useMemo(
        () => selectableStores.map((store) => store.id),
        [selectableStores]
    );

    const selectableStoreIdSet = React.useMemo(
        () => new Set(selectableStoreIds),
        [selectableStoreIds]
    );

    const selectedAvailableCount = React.useMemo(
        () => tempSelectedStoreIds.filter((id) => selectableStoreIdSet.has(id)).length,
        [tempSelectedStoreIds, selectableStoreIdSet]
    );

    const tempAllSelected =
        selectableStoreIds.length > 0 &&
        selectedAvailableCount === selectableStoreIds.length;

    const tempIndeterminate =
        selectedAvailableCount > 0 &&
        selectedAvailableCount < selectableStoreIds.length;

    const tempSelectedStoreNames = React.useMemo(() => {
        const storeById = new Map(storesList.map((store) => [store.id, store]));

        return tempSelectedStoreIds
            .map((id) => {
                const store = storeById.get(id);

                if (!store) return null;

                return {
                    label: store.name,
                    value: store.name,
                };
            })
            .filter(Boolean);
    }, [tempSelectedStoreIds, storesList]);

    const manageSelectAll = React.useCallback(
        (selectAll) => {
            if (readOnly) return;
            setTempSelectedStoreIds(selectAll ? selectableStoreIds : []);
        },
        [selectableStoreIds, readOnly]
    );

    const handleSortingParamChange = (sortField, sortDir) => {
        const sortOrder = sortDir === "asc" ? "desc" : "asc";

        setSortConfig((prev) => {
            const updated = { ...prev };

            Object.keys(updated).forEach((key) => {
                updated[key] = {
                    ...updated[key],
                    active: key === sortField,
                };
            });

            updated[sortField].dir = sortOrder;
            return updated;
        });
    };

    const downloadExcelFile = () => {
        // Build header row based on which optional columns are visible
        const headers = ["Store Name", "Street & No.", "ZIP Code", "City", "Region"];
        // Keep exported columns aligned with the columns visible in the table.
        if (showOwnerColumn) {
            headers.push("Owner");
        }
        if (showRetailerColumn) {
            headers.push("Retailer");
        }
        if (showListedFilter) {
            headers.push("Listed");
        }

        const data = [headers];

        // "filtered" = current table dataset (filters applied). "selected" = all checked stores, ignoring filters.
        const exportRows =
            excelExportScope === "selected"
                ? storesList.filter((store) => tempSelectedStoreIds.includes(store.id))
                : sortedListItems;

        exportRows.forEach((store) => {
            const row = [
                store.name,
                store.address,
                store.zipCode,
                store.city,
                store.region?.name ?? "",
            ];

            if (showOwnerColumn) {
                row.push(store.company?.name ?? "");
            }
            if (showRetailerColumn) {
                row.push(store.root_retailer_company?.name ?? "");
            }

            if (showListedFilter === true) {
                row.push(store.listing_check_saved === true ? "Yes" : "No");
            }

            data.push(row);
        });

        const workbook = xlsx.utils.book_new();
        const worksheet = xlsx.utils.aoa_to_sheet(data);
        xlsx.utils.book_append_sheet(workbook, worksheet, "Sheet1");

        const currentDate = checkTodayDateWithFormat("YYYYMMDD");
        const exportFileName =
            excelFileName || `${currentDate}_StoreOverview.xlsx`;

        xlsx.writeFile(workbook, exportFileName);
    };

    // Drives both the Owner column and the Owner filter so the two can never disagree
    const showOwnerColumn = canViewStoreOwner();

    const columns = React.useMemo(() => {
        const cols = [
            {
                id: "select",
                headerAlign: "center",
                align: "center",
                header: (
                    <ListingCellCenter>
                        <StyledWhiteCheckbox
                            size="small"
                            disabled={readOnly}
                            checked={tempAllSelected}
                            onChange={(e) => manageSelectAll(e.target.checked)}
                            indeterminate={tempIndeterminate}
                        />
                    </ListingCellCenter>
                ),
                render: (store) => (
                    <ListingCellCenter>
                        <StyledCheckbox
                            size="small"
                            disabled={readOnly || store.available === false}
                            checked={tempSelectedStoreIds.includes(store.id)}
                            onChange={(e) =>
                                handleStoreCheckboxChange(store, e.target.checked)
                            }
                        />
                    </ListingCellCenter>
                ),
            },
            {
                id: "name",
                header: (
                    <ListingHeaderContent
                        onClick={() =>
                            handleSortingParamChange("name", sortConfig.name.dir)
                        }
                        style={{ cursor: "pointer" }}
                    >
                        Store Name
                        <SortIcon direction={sortConfig.name.dir} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list wrapLargeText",
                render: (store) => (
                    <ListingCellLeft>{store.name}</ListingCellLeft>
                ),
            },
            {
                id: "address",
                header: (
                    <ListingHeaderContent
                        onClick={() =>
                            handleSortingParamChange("address", sortConfig.address.dir)
                        }
                        style={{ cursor: "pointer" }}
                    >
                        Street &amp; No.
                        <SortIcon direction={sortConfig.address.dir} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list",
                render: (store) => (
                    <ListingCellLeft>{store.address}</ListingCellLeft>
                ),
            },
            {
                id: "zipCode",
                cellSx: { width: "148px" },
                headerSx: { width: "148px" },
                header: (
                    <ListingHeaderContent
                        onClick={() =>
                            handleSortingParamChange("zipcode", sortConfig.zipcode.dir)
                        }
                        style={{ cursor: "pointer" }}
                    >
                        ZIP Code
                        <SortIcon direction={sortConfig.zipcode.dir} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list",
                render: (store) => (
                    <ListingCellLeft>{store.zipCode}</ListingCellLeft>
                ),
            },
            {
                id: "city",
                header: (
                    <ListingHeaderContent
                        onClick={() =>
                            handleSortingParamChange("city", sortConfig.city.dir)
                        }
                        style={{ cursor: "pointer" }}
                    >
                        City
                        <SortIcon direction={sortConfig.city.dir} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list",
                render: (store) => (
                    <ListingCellLeft>{store.city}</ListingCellLeft>
                ),
            },
            {
                id: "region",
                header: (
                    <ListingHeaderContent
                        onClick={() =>
                            handleSortingParamChange("region", sortConfig.region.dir)
                        }
                        style={{ cursor: "pointer" }}
                    >
                        Region
                        <SortIcon direction={sortConfig.region.dir} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list wrapLargeText",
                render: (store) => (
                    <ListingCellLeft>{store.region?.name}</ListingCellLeft>
                ),
            },
        ];

        if (showOwnerColumn) {
            cols.push({
                id: "company",
                header: (
                    <ListingHeaderContent
                        onClick={() =>
                            handleSortingParamChange("owner", sortConfig.owner.dir)
                        }
                        style={{ cursor: "pointer" }}
                    >
                        Owner
                        <SortIcon direction={sortConfig.owner.dir} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list wrapLargeText",
                render: (store) => (
                    <ListingCellLeft>{store.company?.name}</ListingCellLeft>
                ),
            });
        }

        // Root retailer — only for users who see the bottom-nav retailer dropdown
        if (showRetailerColumn) {
            cols.push({
                id: "retailer",
                header: (
                    <ListingHeaderContent
                        onClick={() =>
                            handleSortingParamChange("retailer", sortConfig.retailer.dir)
                        }
                        style={{ cursor: "pointer" }}
                    >
                        Retailer
                        <SortIcon direction={sortConfig.retailer.dir} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list wrapLargeText",
                render: (store) => (
                    <ListingCellLeft>
                        {store.root_retailer_company?.name || ""}
                    </ListingCellLeft>
                ),
            });
        }

        if (showListedFilter) {
            cols.push({
                id: "listed",
                headerAlign: "center",
                align: "center",
                header: (
                    <ListingHeaderContent
                        onClick={() =>
                            handleSortingParamChange("listed", sortConfig.listed.dir)
                        }
                        style={{ cursor: "pointer" }}
                    >
                        Listed
                        <SortIcon direction={sortConfig.listed.dir} />
                    </ListingHeaderContent>
                ),
                render: (store) => (
                    <ListingCellCenter>
                        {store.listing_check_saved === true ? (
                            <Check fontSize="small" sx={{ color: "#AF3241" }} />
                        ) : (
                            <Close fontSize="small" sx={{ color: "#AF3241" }} />
                        )}
                    </ListingCellCenter>
                ),
            });
        }

        return cols;
    }, [
        sortConfig,
        tempSelectedStoreIds,
        tempAllSelected,
        tempIndeterminate,
        manageSelectAll,
        handleStoreCheckboxChange,
        showOwnerColumn,
        showRetailerColumn,
        showListedFilter,
        readOnly,
    ]);

    React.useEffect(() => {
        if (!open) return;

        if (!isReduxManaged) {
            setTempSelectedStoreIds(Array.isArray(selectedValues) ? selectedValues : []);
            return;
        }

        const reduxSelection = storeDetails[storeSelectionReduxKey] || {};
        setTempSelectedStoreIds(reduxSelection.store_ids || []);
    }, [open]);

    const isApplyDisabled =
        readOnly || (disableApplyWhenEmpty && tempSelectedStoreIds.length === 0);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            disableRestoreFocus={disableRestoreFocus}
        >
            <DialogTitle sx={{ pr: 6 }}>
                <Typography variant="h6">{title}</Typography>
            </DialogTitle>

            <DialogContent>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                    <MultiSelect
                        label="Store Name"
                        options={storeOptions}
                        usedFor="filter"
                        selectionCheck={(o, v) => o.id === v.id}
                        defaultVal={storeNameFilter}
                        onChangeCall={setStoreNameFilter}
                    />

                    <MultiSelect
                        label="Location"
                        options={locationOptions}
                        usedFor="filter"
                        selectionCheck={(o, v) => o.id === v.id}
                        defaultVal={locationFilter}
                        onChangeCall={setLocationFilter}
                        showOnlySelectedUntilSearch={true}
                        additionalIconWithTooltip={true}
                        additionalIconMarkup={LocationFilterHelpIconWithTooltip}
                    />

                    {/* Owner filter follows the Owner column, even when only one owner exists */}
                    {showOwnerColumn && (
                        <MultiSelect
                            label="Owner"
                            options={ownerOptions}
                            usedFor="filter"
                            selectionCheck={(o, v) => o.id === v.id}
                            defaultVal={ownerFilter}
                            onChangeCall={setOwnerFilter}
                        />
                    )}

                    {/* Same gating as the Retailer table column; Multiselect shows "All Results" when empty */}
                    {showRetailerColumn && (
                        <MultiSelect
                            label="Retailer"
                            options={retailerFilterOptions}
                            usedFor="filter"
                            selectionCheck={(o, v) => o.id === v.id}
                            defaultVal={retailerFilter}
                            onChangeCall={setRetailerFilter}
                        />
                    )}

                    {showListedFilter && (
                        <SingleSelect
                            label={"Listed"}
                            elemId={"listedFilterOptions"}
                            enableFullWidth={false}
                            options={[
                                { label: "Yes", value: "yes" },
                                { label: "No", value: "no" },
                            ]}
                            usedFor="filter"
                            defaultVal={listedFilter}
                            selectionCheck={(option, value) => option.value === value.value}
                            onChangeCall={setListedFilter}
                        />
                    )}

                    <Stack direction="row" spacing={2} alignItems="center">
                        {hasFilters ? (
                            <ActionButton
                                onClick={applyFilters}
                                label="Apply"
                                icon={<FilterAlt />}
                                sx={{ mt: 2, mb: 0 }}
                            />
                        ) : (
                            <DisabledButton label="Apply" icon={<FilterAlt />} />
                        )}

                        {storeDetails.detailed_store_list_filter_applied ? (
                            <LightGreyButton
                                onClick={clearFilters}
                                label="Clear"
                                icon={<Clear />}
                            />
                        ) : (
                            <DisabledButton label="Clear" icon={<Clear />} />
                        )}
                    </Stack>
                </Stack>

                <Box height={LISTING_ACTION_TO_TABLE_GAP} />

                <ListingDataTableForModal
                    columns={columns}
                    rows={storeListitems}
                    rowKey={(store, idx) =>
                        `${store.number_store_cyreen ?? idx}_store_capacity`
                    }
                    emptyText="No results"
                    pagination={null}
                    onPageChange={null}
                    onPageSizeChange={null}
                />

                <Box height={25} />

                <SpaceBetweenStack
                    direction="row"
                    spacing={2}
                    width="100%"
                    justifyContent="space-between"
                    display="flex"
                    alignItems="center"
                >
                    <StyledDarkRedInformationTypography variant="body2">
                        Selected {tempSelectedStoreIds.length} of {storesList.length}
                    </StyledDarkRedInformationTypography>

                    {enableExcelDownload &&
                        (excelExportScope === "selected"
                            ? tempSelectedStoreIds.length > 0
                            : sortedListItems.length > 0) && (
                        <Stack direction="row" alignItems="center" spacing={-1}>
                            <TextButton
                                onClick={(e) => {
                                    e.preventDefault();
                                    downloadExcelFile();
                                }}
                                label="Download Excel"
                                icon={<Download />}
                            />
                            <HtmlTooltip
                                title={
                                    <Box sx={{ maxWidth: 220 }}>
                                        {excelExportScope === "selected" && (
                                            <Typography variant="body2">
                                                Exports list of all selected stores.
                                            </Typography>
                                        )}
                                        <Typography variant="body2">
                                            The export reflects the current state of data at the time of download.
                                            Data in CAP Explorer may change over time.
                                        </Typography>
                                    </Box>
                                }
                            >
                                <IconContainer>
                                    <Info fontSize="small" sx={{ marginTop: 1 }} />
                                </IconContainer>
                            </HtmlTooltip>
                        </Stack>
                    )}
                </SpaceBetweenStack>

                <SpaceBetweenStack
                    direction="row"
                    spacing={2}
                    width="100%"
                    justifyContent="space-between"
                    display="flex"
                >
                    <Pagination
                        totalCount={listItems.length}
                        reduxPaginationKeyword="store_selection_pagination"
                        reduxStartKeyword="store_list_start"
                        reduxEndKeyword="store_list_end"
                    />
                </SpaceBetweenStack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Stack direction="row" spacing={2}>
                    <CloseButton
                        onClick={handleCancel}
                        label="Cancel"
                        icon={<Close />}
                    />

                    {isApplyDisabled ? (
                        <DisabledButton label="Apply" icon={<Save />} />
                    ) : (
                        <ActionButton
                            onClick={handleApply}
                            label="Apply"
                            icon={<Save />}
                        />
                    )}
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default StoreDetailTable;
